import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Spinner from '../components/Spinner/spinner';
import Modal from '../components/modal/modal';
import Tooltip from '../components/tooltip/Tooltip';
import { routes } from '../routes/routePaths';
import { useToast } from '../components/toast/toast';
import { COMITE_LEVEL_ROLES } from '../utils/Constants';
import { hasAnyRole, toNumber } from '../utils/Utils';
import {
  getRegulacionSepyme,
  updateRegulacionSepyme,
  RegulacionSepymeData,
} from '../services/RegulacionSepymeService';

type FieldKey = 'authorizedRiskFund' | 'maximumLeverage' | 'maximumPerPartner';

type FormValues = {
  authorizedRiskFund: string;
  maximumLeverage: string;
  maximumPerPartner: string;
};

type FieldErrors = Partial<Record<FieldKey, string>>;

const EMPTY_FORM: FormValues = {
  authorizedRiskFund: '',
  maximumLeverage: '',
  maximumPerPartner: '',
};

const formatAmountDisplay = (value: number | null): string => {
  if (value === null || value === undefined) return '';
  const isInteger = Math.abs(value % 1) < 0.000001;
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const formatLeverageDisplay = (value: number | null): string => {
  if (value === null || value === undefined) return '';
  return String(Math.trunc(value));
};

const valuesFromData = (data: RegulacionSepymeData): FormValues => ({
  authorizedRiskFund: formatAmountDisplay(data.authorizedRiskFund),
  maximumLeverage: formatLeverageDisplay(data.maximumLeverage),
  maximumPerPartner: formatAmountDisplay(data.maximumPerPartner),
});

const parseNumericValue = (field: FieldKey, rawValue: string): number | null => {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  if (field === 'maximumLeverage') {
    const parsed = Number(trimmed.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = toNumber(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const validateField = (field: FieldKey, rawValue: string): string | undefined => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return 'Este campo es obligatorio';
  }

  const value = parseNumericValue(field, trimmed);
  if (value === null) {
    return field === 'maximumLeverage'
      ? 'Debe ser un entero entre 1 y 100'
      : field === 'maximumPerPartner'
        ? 'Debe ser un porcentaje entre 0 y 100'
        : 'Debe ser mayor a 0';
  }

  if (field === 'authorizedRiskFund' && value <= 0) {
    return 'Debe ser mayor a 0';
  }

  if (field === 'maximumLeverage') {
    if (!Number.isInteger(value) || value < 1 || value > 100) {
      return 'Debe ser un entero entre 1 y 100';
    }
  }

  if (field === 'maximumPerPartner' && (value <= 0 || value > 100)) {
    return 'Debe ser un porcentaje entre 0 y 100';
  }

  return undefined;
};

const validateForm = (values: FormValues): FieldErrors => {
  const errors: FieldErrors = {};
  (Object.keys(values) as FieldKey[]).forEach((field) => {
    const error = validateField(field, values[field]);
    if (error) {
      errors[field] = error;
    }
  });
  return errors;
};

const RegulacionSepyme = () => {
  const navigate = useNavigate();
  const canAccess = hasAnyRole(COMITE_LEVEL_ROLES);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);
  const [originalValues, setOriginalValues] = useState<FormValues>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRegulacionSepyme();
      if (response.success && response.data) {
        const mappedValues = valuesFromData(response.data);
        setFormValues(mappedValues);
        setOriginalValues(mappedValues);
        setFieldErrors({});
      } else {
        useToast.error({
          title: response.message || 'Error al cargar la configuración',
          id: 'regulacion-sepyme-load-error',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch {
      useToast.error({
        title: 'Error al cargar la configuración',
        id: 'regulacion-sepyme-load-error',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      useToast.error({
        title: 'No tiene permisos para acceder a este recurso.',
        id: 'regulacion-sepyme-permission-error',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      navigate(routes.settings);
      return;
    }

    loadConfiguration();
  }, [canAccess, loadConfiguration, navigate]);

  const hasChanges = useMemo(
    () => JSON.stringify(formValues) !== JSON.stringify(originalValues),
    [formValues, originalValues]
  );

  const validationErrors = useMemo(() => validateForm(formValues), [formValues]);
  const isFormValid = Object.keys(validationErrors).length === 0;
  const isSaveDisabled = saving || !hasChanges || !isFormValid;

  const handleFieldChange = (field: FieldKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setFormValues((current) => ({
      ...current,
      [field]: nextValue,
    }));
    setFieldErrors((current) => ({
      ...current,
      [field]: validateField(field, nextValue),
    }));
  };

  const handleFieldFocus = (field: FieldKey) => () => {
    setFormValues((current) => {
      const parsed = parseNumericValue(field, current[field]);
      return {
        ...current,
        [field]: parsed === null ? '' : String(parsed),
      };
    });
  };

  const handleFieldBlur = (field: FieldKey) => () => {
    setFormValues((current) => {
      const parsed = parseNumericValue(field, current[field]);
      let formatted = '';
      if (parsed !== null) {
        formatted =
          field === 'maximumLeverage'
            ? formatLeverageDisplay(parsed)
            : formatAmountDisplay(parsed);
      }
      setFieldErrors((prev) => ({
        ...prev,
        [field]: validateField(field, formatted),
      }));
      return {
        ...current,
        [field]: formatted,
      };
    });
  };

  const navigateBack = () => {
    navigate(routes.settings);
  };

  const handleGoBack = () => {
    if (hasChanges) {
      setShowDiscardModal(true);
      return;
    }
    navigateBack();
  };

  const handleSave = async () => {
    const errors = validateForm(formValues);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload = {
      authorizedRiskFund: parseNumericValue('authorizedRiskFund', formValues.authorizedRiskFund)!,
      maximumLeverage: parseNumericValue('maximumLeverage', formValues.maximumLeverage)!,
      maximumPerPartner: parseNumericValue('maximumPerPartner', formValues.maximumPerPartner)!,
    };

    setSaving(true);
    try {
      const response = await updateRegulacionSepyme(payload);
      if (response.success && response.data) {
        const mappedValues = valuesFromData(response.data);
        setFormValues(mappedValues);
        setOriginalValues(mappedValues);
        setFieldErrors({});
        useToast.success({
          title: 'Configuración actualizada correctamente.',
          id: 'regulacion-sepyme-save-success',
          description: '',
          closeButton: true,
          autoClose: 4000,
        });
      } else {
        useToast.error({
          title: response.message || 'Error al actualizar la configuración',
          id: 'regulacion-sepyme-save-error',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch {
      useToast.error({
        title: 'Error al actualizar la configuración',
        id: 'regulacion-sepyme-save-error',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) {
    return null;
  }

  return (
    <>
      <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
        <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
          <Button
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label='Volver a configuración'
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Regulación SePyME
          </H>
        </Card>

        {loading ? (
          <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
            <Spinner size='lg' />
          </Card>
        ) : (
          <>
            <Card spacing='4' margin={{ bottom: '4' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <Input
                  label='Fondo de riesgo autorizado *'
                  value={formValues.authorizedRiskFund}
                  onChange={handleFieldChange('authorizedRiskFund')}
                  onFocus={handleFieldFocus('authorizedRiskFund')}
                  onBlur={handleFieldBlur('authorizedRiskFund')}
                  inputStatus={fieldErrors.authorizedRiskFund ? 'error' : 'normal'}
                  message={fieldErrors.authorizedRiskFund}
                  placeholder='0'
                  size='md'
                  onlyNumbers
                />
                <Input
                  label='Apalancamiento máximo *'
                  value={formValues.maximumLeverage}
                  onChange={handleFieldChange('maximumLeverage')}
                  onFocus={handleFieldFocus('maximumLeverage')}
                  onBlur={handleFieldBlur('maximumLeverage')}
                  inputStatus={fieldErrors.maximumLeverage ? 'error' : 'normal'}
                  message={fieldErrors.maximumLeverage}
                  placeholder='0'
                  size='md'
                  onlyNumbers
                />
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      label='Máximo por partícipe (%) *'
                      value={formValues.maximumPerPartner}
                      onChange={handleFieldChange('maximumPerPartner')}
                      onFocus={handleFieldFocus('maximumPerPartner')}
                      onBlur={handleFieldBlur('maximumPerPartner')}
                      inputStatus={fieldErrors.maximumPerPartner ? 'error' : 'normal'}
                      message={fieldErrors.maximumPerPartner}
                      placeholder='0'
                      size='md'
                      onlyNumbers
                    />
                  </div>
                  <H withMargin={false} type='3' weight='bold' modifier='strong'>
                    %
                  </H>
                </div>
              </div>
            </Card>

            <Card display='flex' justifyContent='flex-end'>
              <Button
                variant='primary'
                size='lg'
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Card>
          </>
        )}
      </Card>

      <Modal
        isOpen={showDiscardModal}
        title='Tiene cambios sin guardar. ¿Desea descartarlos?'
        primaryText='Descartar'
        secondaryText='Cancelar'
        onPrimaryAction={() => {
          setShowDiscardModal(false);
          navigateBack();
        }}
        onSecondaryAction={() => setShowDiscardModal(false)}
        onClose={() => setShowDiscardModal(false)}
      />
    </>
  );
};

export default RegulacionSepyme;
